import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { login } from '../../services/authService';
import { ChevronLeft, Eye, EyeOff, Loader2 } from 'lucide-react';

// Define validation schema with Yup
const schema = yup.object({
  username: yup.string().required('Username is required'),
  password: yup.string().required('Password is required').min(6, 'Password must be at least 8 characters long')
}).required();

const SignIn = () => {
  const navigate = useNavigate();
  const [loginError, setLoginError] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Initialize React Hook Form with validation schema
  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(schema)
  });

  const togglePasswordVisibility = () => {
    setPasswordVisible(prevState => !prevState);
  };

  const onSubmit = async (data) => {
    try {
      setLoginError('');
      setIsLoading(true);
      await login(data.username, data.password, 'client');
      navigate("/market", {
        state: { loginMessage: "Login successful!" }
      });
    } catch (error) {
      if (error.response && error.response.data && error.response.data.message) {
        setLoginError(error.response.data.message);
      } else if (error.message) {
        setLoginError(error.message);
      } else {
        setLoginError('An unexpected error occurred. Please try again.');
      }
      setIsLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate("/");
  };

  return (
    <div className="flex items-center justify-center flex-col bg-black min-h-screen p-0">
      <div className="relative">
        <div className="h-screen overflow-y-auto p-4 pb-20 max-w-sm min-w-[375px] w-full bg-white">
          <div className="flex flex-col justify-center h-full">
            <span className="text-gray-700 font-bold -mt-[10px] mb-[60px] -ml-[5px]">
              <ChevronLeft size={20} onClick={handleGoBack} />
            </span>
            <p className="text-[32px] leading-[40px] mb-[10px] mt-0 text-black font-semibold"
              style={{ fontFamily: "Lato, sans-serif" }}>
              Sign In
            </p>

            <p className="text-[14px] font-semibold leading-[22px] mb-[30px] text-gray-700"
              style={{ fontFamily: "Lato, sans-serif" }}>
              This is Virtual Trading platform only for educational purpose. and Developers are not responsible for any losses/profits if incurred.
            </p>
            <div>
              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <div className="mb-4">
                  <label
                    className="text-[14px] leading-[21px] text-gray-800 font-lato mb-2 block"
                    htmlFor="exampleForm.ControlInput1"
                  >
                    Username
                  </label>
                  <input
                    placeholder="Enter your username"
                    type="text"
                    id="username"
                    {...register('username')}
                    className="w-full bg-gray-300 focus:border-blue-300 focus:outline-none rounded-full px-4 py-2"
                  />
                  {errors.username && <span className="text-red-500 text-sm">{errors.username.message}</span>}
                </div>

                <div className="mb-2 relative">
                  <label
                    className="text-[14px] leading-[21px] text-gray-800 font-lato mb-2 block"
                    htmlFor="formGroupPassword"
                  >
                    Password
                  </label>
                  <input
                    placeholder="Password"
                    type={passwordVisible ? "text" : "password"}
                    id="password"
                    {...register('password')}
                    className="w-full bg-gray-300 focus:border-blue-300 focus:outline-none rounded-full px-4 py-2"
                  />
                  {passwordVisible ? (
                    <EyeOff
                      className="absolute right-2 top-9 text-gray-400 cursor-pointer"
                      size={24}
                      onClick={togglePasswordVisibility}
                    />
                  ) : (
                    <Eye
                      className="absolute right-2 top-9 text-gray-400 cursor-pointer"
                      size={24}
                      onClick={togglePasswordVisibility}
                    />
                  )}
                  {errors.password && <span className="text-red-500 text-sm">{errors.password.message}</span>}
                </div>
                {loginError && <div className="mb-4 text-red-500 text-sm">{loginError}</div>}
                {/* <div className="flex justify-end">
                  <Link to="/forgot-password" className="text-[14px] font-lato font-semibold text-[#536895] leading-[17px]">
                    Forgot your password?
                  </Link>
                </div> */}

                <div className="mt-8">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="bg-[#536895] w-full border border-[#536895] rounded-full text-white text-base font-semibold py-3 px-12 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isLoading && <Loader2 size={20} className="animate-spin" />}
                    {isLoading ? "Signing In..." : "Sign In"}
                  </button>
                </div>

                <div className="flex justify-center mt-8">
                  <Link to="/signup" className="text-[14px] font-lato font-semibold text-[#536895]">
                    Create new account
                  </Link>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default SignIn;
